<script setup lang="ts">
import { nextTick, ref, watch } from 'vue';
import { confirmState, settleConfirm } from '../lib/confirm';

const confirmButton = ref<HTMLButtonElement | null>(null);
const reasonField = ref<HTMLTextAreaElement | null>(null);

watch(
  () => confirmState.open,
  async (open) => {
    if (open) {
      await nextTick();
      (reasonField.value ?? confirmButton.value)?.focus();
    }
  },
);
</script>

<template>
  <Teleport to="body">
    <div v-if="confirmState.open" class="confirm-layer" @keydown.esc="settleConfirm(false)">
      <div class="confirm-scrim" @click="settleConfirm(false)" />
      <div class="confirm" role="alertdialog" aria-modal="true" :aria-label="confirmState.title">
        <h2>{{ confirmState.title }}</h2>
        <p>{{ confirmState.body }}</p>
        <label v-if="confirmState.reasonLabel" class="field">
          <span class="field-label">{{ confirmState.reasonLabel }}</span>
          <textarea ref="reasonField" v-model="confirmState.reason" class="textarea" rows="2" />
        </label>
        <div class="confirm-actions">
          <button class="btn" @click="settleConfirm(false)">Vazgeç</button>
          <button
            ref="confirmButton"
            class="btn"
            :class="confirmState.danger ? 'btn-danger' : 'btn-primary'"
            :disabled="!!confirmState.reasonLabel && confirmState.reason.trim().length < 3"
            @click="settleConfirm(true)"
          >
            {{ confirmState.confirmLabel }}
          </button>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<style scoped>
.confirm-layer {
  position: fixed;
  inset: 0;
  z-index: 60;
  display: grid;
  place-items: center;
  padding: 1rem;
}
.confirm-scrim {
  position: absolute;
  inset: 0;
  background: rgb(17 20 25 / 0.4);
}
.confirm {
  position: relative;
  display: grid;
  gap: 0.6rem;
  width: min(26rem, 100%);
  padding: 1.4rem 1.5rem 1.2rem;
  background: var(--surface);
  border-radius: var(--radius-l);
  box-shadow: var(--shadow-pop);
}
.confirm p {
  color: var(--ink-2);
  font-size: var(--t-s);
}
.confirm-actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
  margin-top: 0.6rem;
}
@media (max-width: 760px) {
  /* A sheet from the bottom edge, its buttons within thumb reach. */
  .confirm-layer {
    place-items: end stretch;
    padding: 0;
  }
  .confirm {
    width: 100%;
    padding: 1.3rem 1.1rem calc(1rem + env(safe-area-inset-bottom));
    border-radius: var(--radius-l) var(--radius-l) 0 0;
  }
  .confirm-actions {
    flex-direction: column-reverse;
  }
  .confirm-actions :deep(.btn),
  .confirm-actions .btn {
    width: 100%;
  }
}
</style>
